/**
 * Sign Up Landing Page
 *
 * Shown when the user is authenticated but cannot use the practitioner app yet.
 * Copy follows ADR-0023 / ticket 11 state table.
 */

import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { UserRole } from '@/components/authnz/types/rbac';
import { resolvePostLoginPath, userHasAppAccess } from '@/lib/access-state';
import { resolveSignupGateCopy, type SignupGateCopy } from '@/lib/signup-gate-copy';
import { fetchSessionStatus, normalizeSessionResponse } from '@/lib/session-fetch';
import {
  PAYMENT_PENDING_POLL_INTERVAL_MS,
  reconcileRolesWhilePaymentPending,
  shouldContinuePaymentPendingPoll,
} from '@/lib/access/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { CheckCircle, ArrowRight, LogOut, Shield } from 'lucide-react';
import { signOut } from 'firebase/auth';
import { auth } from '@/lib/firebase-config';
import { getCorrelationId } from '@/lib/logging';
import logger from '@/lib/logging';
import { BROWSE_PLANS_PATH } from '@/components/subscriptions/lib/plans-path';

export function SignUpLandingPage() {
  const { user: currentUser, serverUser, loading } = useAuth();
  const navigate = useNavigate();
  const [gateCopy, setGateCopy] = useState<SignupGateCopy>(() =>
    resolveSignupGateCopy({ roles: [], accessState: 'invited-waiting' }),
  );

  useEffect(() => {
    if (loading) return;
    const roles = (serverUser?.roles ?? []) as UserRole[];
    if (userHasAppAccess(roles)) {
      logger.debug('[SignUpLandingPage] User has app access, redirecting to dashboard', {
        email: currentUser?.email ?? serverUser?.email,
        roles,
      });
      navigate(resolvePostLoginPath(roles), { replace: true });
    }
  }, [loading, serverUser?.roles, currentUser?.email, serverUser?.email, navigate]);

  useEffect(() => {
    if (gateCopy.variant !== 'payment-pending') {
      return;
    }

    const startedAt = Date.now();
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    async function pollReconcile() {
      if (cancelled || !shouldContinuePaymentPendingPoll(startedAt)) {
        return;
      }
      try {
        await reconcileRolesWhilePaymentPending();
        const sessionResponse = await fetchSessionStatus({ retries: 0 });
        if (sessionResponse.ok) {
          const data = await sessionResponse.json();
          const session = normalizeSessionResponse(data);
          if (session.accessState === 'ok' || userHasAppAccess(session.roles ?? [])) {
            navigate(resolvePostLoginPath(session.roles ?? []), { replace: true });
            return;
          }
        }
      } catch (error) {
        logger.debug('[SignUpLandingPage] payment-pending reconcile poll failed', error);
      }
      if (!cancelled && shouldContinuePaymentPendingPoll(startedAt)) {
        timer = setTimeout(() => {
          void pollReconcile();
        }, PAYMENT_PENDING_POLL_INTERVAL_MS);
      }
    }

    void pollReconcile();

    return () => {
      cancelled = true;
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [gateCopy.variant, navigate]);

  useEffect(() => {
    let cancelled = false;

    async function loadGateCopy() {
      const roles = (serverUser?.roles ?? []) as UserRole[];
      let accessState: string | undefined;
      let reasonKind: string | undefined;

      try {
        const response = await fetchSessionStatus({ retries: 1 });
        if (response.ok) {
          const data = await response.json();
          const session = normalizeSessionResponse(data);
          accessState = session.accessState;
          reasonKind =
            typeof data.reason === 'object' && data.reason
              ? String((data.reason as { kind?: string }).kind ?? '')
              : undefined;
        }
      } catch (error) {
        logger.debug('[SignUpLandingPage] Session fetch for gate copy failed', error);
      }

      if (cancelled) return;

      setGateCopy(
        resolveSignupGateCopy({
          roles,
          accessState,
          emailVerified: currentUser?.emailVerified ?? true,
          reasonKind,
        }),
      );
    }

    void loadGateCopy();
    return () => {
      cancelled = true;
    };
  }, [serverUser?.roles, currentUser?.emailVerified]);

  useEffect(() => {
    document.documentElement.classList.add('allow-document-scroll');
    document.body.classList.add('allow-document-scroll');
    const root = document.getElementById('root');
    root?.classList.add('allow-document-scroll');

    return () => {
      document.documentElement.classList.remove('allow-document-scroll');
      document.body.classList.remove('allow-document-scroll');
      root?.classList.remove('allow-document-scroll');
    };
  }, []);

  const handleSignOut = async () => {
    try {
      if (!auth) {
        logger.error('Auth not initialized');
        return;
      }
      await signOut(auth);
      navigate('/login');
    } catch (error) {
      logger.error('Error signing out:', error);
    }
  };

  const correlationId = getCorrelationId();
  const showBenefits = gateCopy.variant === 'invited-waiting' || gateCopy.variant === 'payment-pending';

  return (
    <div className="min-h-dvh bg-gradient-to-br from-background via-muted/20 to-background px-4 py-6 pb-24 md:flex md:items-center md:justify-center md:py-8 md:pb-8">
      <div className="mx-auto w-full max-w-2xl">
        <div className="mb-6 text-center md:mb-8">
          <Shield className="mx-auto mb-3 h-12 w-12 text-primary md:mb-4 md:h-16 md:w-16" />
          <h1 className="mb-2 text-2xl font-bold sm:text-3xl md:text-4xl">Welcome to Harry Biomechanics</h1>
          <p className="text-base text-muted-foreground md:text-lg">
            Professional Biomechanics Analysis Platform
          </p>
        </div>

        <Card className="shadow-2xl">
          <CardHeader>
            <CardTitle className="text-2xl">{gateCopy.title}</CardTitle>
            <CardDescription className="text-base">
              {currentUser?.email || serverUser?.email ? (
                <>
                  You&apos;re signed in as{' '}
                  <span className="font-semibold text-foreground">
                    {currentUser?.email ?? serverUser?.email}
                  </span>
                </>
              ) : (
                'Sign-in required'
              )}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="rounded-lg border border-border bg-muted/50 p-6">
              <p className="mb-2 text-center text-lg">{gateCopy.message}</p>
              {gateCopy.detail ? (
                <p className="text-center text-muted-foreground">{gateCopy.detail}</p>
              ) : null}
            </div>

            {showBenefits ? (
              <div>
                <h3 className="mb-4 text-lg font-semibold">What you&apos;ll get with access</h3>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-success-text" />
                    <div>
                      <p className="font-medium">Complete Client Management</p>
                      <p className="text-sm text-muted-foreground">
                        Manage client profiles, assessments, and progress tracking
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-success-text" />
                    <div>
                      <p className="font-medium">Advanced Posture Analysis</p>
                      <p className="text-sm text-muted-foreground">
                        AI-powered posture assessment with detailed insights
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-success-text" />
                    <div>
                      <p className="font-medium">SOP Assessment Tools</p>
                      <p className="text-sm text-muted-foreground">
                        Comprehensive assessment protocols and reporting
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ) : null}

            <div className="flex flex-col gap-3 pt-4 sm:flex-row">
              {gateCopy.variant === 'clients-only' ? (
                <Button asChild className="h-12 flex-1">
                  <Link to="/self-assessment">Open the client experience</Link>
                </Button>
              ) : null}
              {gateCopy.variant === 'invited-waiting' ? (
                <Button asChild className="h-12 flex-1">
                  <Link to={BROWSE_PLANS_PATH}>See plans</Link>
                </Button>
              ) : null}
              <Button onClick={handleSignOut} variant="outline" className="h-12 flex-1">
                <LogOut className="mr-2 h-4 w-4" />
                Sign Out
              </Button>
            </div>

            <div className="border-t pt-4 text-center text-sm text-muted-foreground">
              <p className="mb-1">
                Reference: {gateCopy.variant}
                {correlationId ? ` · ${correlationId}` : ''}
              </p>
              <Button onClick={handleSignOut} variant="link" className="text-primary">
                Sign out and try a different account
                <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
